import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Radar, Sparkles, GitBranch, LayoutGrid, BrainCircuit } from "lucide-react";
import { TECH, COMPANY } from "../../data/whitespaces";
import { Reveal, MaskLine } from "../../lib/motionPrimitives";
import EmblemWatermark from "../EmblemWatermark";

const ICONS = {
  CAUSECT: Radar,
  RTNLZE: Sparkles,
  "D:TRACK": GitBranch,
  CAUSE: LayoutGrid,
  CHIP: BrainCircuit,
};

export const Technology = () => {
  const [active, setActive] = useState(TECH.length - 1); // default to CHIP
  const t = TECH[active];
  const Icon = ICONS[t.code];

  return (
    <section
      id="technology"
      data-testid="technology-section"
      className="relative border-t border-white/10 bg-black/20 px-6 py-14 backdrop-blur-sm md:px-12 md:py-20"
    >
      <EmblemWatermark className="-right-16 -top-16 h-[36vw] w-[36vw] max-w-[520px] rotate-6" />
      <div className="mx-auto max-w-[1600px]">
        <div className="mb-8 flex items-center gap-4">
          <span className="font-body text-[11px] uppercase tracking-[0.3em] text-teal">03 — Infrastructure</span>
          <span className="h-px flex-1 bg-white/10" />
          <span className="font-body text-[11px] uppercase tracking-[0.3em] text-white/30">Whitespaces</span>
        </div>

        <div className="grid gap-10 md:grid-cols-[1.3fr_1fr] md:items-end">
          <h2 className="font-display text-4xl font-medium leading-[1.05] tracking-tight text-white md:text-6xl lg:text-7xl">
            <MaskLine className="pb-[0.08em]">A proprietary</MaskLine>
            <MaskLine delay={0.08} className="pb-[0.08em] text-white/40">
              big-data blender.
            </MaskLine>
          </h2>
          <Reveal delay={0.15}>
            <p className="max-w-md font-body text-base leading-relaxed text-white/55">{COMPANY.blender}</p>
          </Reveal>
        </div>

        {/* Interactive explorer */}
        <div className="mt-16 grid gap-4 lg:grid-cols-[0.9fr_1.5fr]">
          {/* Selector list */}
          <div className="flex flex-col gap-2" data-testid="tech-selector">
            {TECH.map((item, i) => {
              const ItemIcon = ICONS[item.code];
              const on = i === active;
              return (
                <button
                  key={item.code}
                  onClick={() => setActive(i)}
                  onMouseEnter={() => setActive(i)}
                  data-testid={`tech-select-${item.code}`}
                  className={`group flex items-center gap-5 rounded-xl border p-5 text-left transition-colors duration-300 md:p-6 ${
                    on ? "border-teal/40 bg-white/[0.04]" : "border-white/10 bg-white/[0.01] hover:border-white/25"
                  }`}
                >
                  <ItemIcon
                    className={on ? "text-teal" : "text-white/50"}
                    size={24}
                    strokeWidth={1.4}
                  />
                  <div className="min-w-0">
                    <div className="font-display text-xl font-semibold tracking-tight text-white md:text-2xl">
                      {item.code}
                    </div>
                    <div className="truncate font-body text-[12px] uppercase tracking-[0.12em] text-white/40">
                      {item.title}
                    </div>
                  </div>
                  <span
                    className={`ml-auto font-body text-[11px] tracking-[0.15em] transition-colors ${
                      on ? "text-teal" : "text-white/20"
                    }`}
                  >
                    0{i + 1}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Detail panel */}
          <div
            className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] p-8 backdrop-blur-xl md:p-12"
            style={{ boxShadow: "inset 0 1px 0 0 rgba(255,255,255,0.05)" }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={t.code}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`tech-detail-${t.code}`}
              >
                <Icon className="text-teal" size={34} strokeWidth={1.3} />
                <h3 className="mt-8 font-display text-4xl font-semibold tracking-tight text-white md:text-5xl">
                  {t.code}
                </h3>
                <p className="mt-2 font-body text-[13px] uppercase tracking-[0.18em] text-teal/80">
                  {t.title}
                </p>
                <p className="mt-6 max-w-xl font-body text-lg leading-relaxed text-white/65">
                  {t.detail}
                </p>

                <div className="mt-8 flex flex-wrap gap-2">
                  {t.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full border border-white/15 px-3 py-1.5 font-body text-[11px] uppercase tracking-[0.1em] text-white/55"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* CHIP extended detail */}
                {t.phases && (
                  <div className="mt-10 border-t border-white/10 pt-8">
                    <div className="mb-5 font-body text-[11px] uppercase tracking-[0.2em] text-white/40">
                      3-Phase optimization cycle
                    </div>
                    <div className="grid gap-3 sm:grid-cols-3">
                      {t.phases.map((p) => (
                        <div key={p.n} className="rounded-lg border border-white/10 bg-obsidian/60 p-5">
                          <div className="font-display text-2xl font-semibold text-ember">{p.n}</div>
                          <div className="mt-1 font-display text-lg text-white">{p.label}</div>
                          <div className="mt-2 font-body text-[13px] leading-relaxed text-white/50">
                            {p.desc}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-6">
                      <div className="mb-3 font-body text-[11px] uppercase tracking-[0.2em] text-white/40">
                        Signals tested (up to 18)
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {t.variables.map((v) => (
                          <span
                            key={v}
                            className="rounded-md bg-teal/10 px-3 py-1.5 font-body text-[12px] text-teal"
                          >
                            {v}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
            <div className="pointer-events-none absolute -right-20 -bottom-20 h-52 w-52 rounded-full bg-teal/10 blur-3xl" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Technology;
